<template>
  <b-modal header-class="ion-bg-modal-header" size="lg"
           centered scrollable no-close-on-backdrop
           v-model="visible">

    <!-- Modal Header -->
    <template #modal-header>
      <div class="fw-bold">{{ $t('settlement.reportTitle') }} {{ $t('common.create') }}</div>
    </template>

    <div class="row">
      <div class="col-xl-12">

        <!-- Title -->
        <div class="row mb-3">
          <div class="col-xl-12">
            <label class="form-label" for="report-create-title">
              <span>{{ $t('settlement.title') }}</span>
              <i class="uil uil-asterisk text-danger ms-1"></i>
            </label>

            <b-form-input class="form-control"
                          type="text"
                          id="report-create-title"
                          v-model="form.title"
                          :class="{ 'is-invalid': submitted && $v.form.title.$error }" />

            <div v-if="submitted && $v.form.title.$error" class="invalid-feedback">
              <span v-if="!$v.form.title.required">{{ $t('validation.titleIsRequired') }}</span>
            </div>
          </div>
        </div>

        <!-- CP -->
        <div class="row mb-5" v-if="$_app.user.roles[0] === 'ADMIN' || $_app.user.roles[0] === 'HQ'">
          <div class="col-xl-6">
            <label class="form-label" for="report-create-cp">
              <span>{{ $t('settlement.cpName') }}</span>
              <i class="uil uil-asterisk text-danger ms-1"></i>
            </label>
            <multiselect id="report-create-cp"
                         v-model="cpId"
                         label="text" track-by="value"
                         :options="cpOptions"
                         :max-height="150"
                         :placeholder="$t('alert.selectCp')"
                         selectLabel=""
                         selectedLabel=""
                         :deselectLabel="$t('common.unselect')"
                         :searchable="false" :allow-empty="false"
                         :class="{ 'is-invalid': submitted && $v.cpId.$error }" />
            
            <div v-if="submitted && $v.cpId.$error" class="invalid-feedback">
              <span v-if="!$v.cpId.required">{{ $t('validation.cpNameIsRequired') }}</span>
            </div>
          </div>
        </div>

        <!-- PDF Upload -->
        <div class="row mb-3">
          <div class="col-xl-12">
            <label class="form-label" for="report-pdf-file">
              <span>{{ $t('common.file') }}</span>
              <i class="uil uil-asterisk text-danger ms-1"></i>
            </label>

            <div class="d-flex">
              <div class="input-group">
                <input class="form-control"
                       type="file"
                       accept="application/pdf"
                       id="report-pdf-file"
                       ref="pdfFileInput"
                       @change="fileCheck"
                       :class="{ 'is-invalid': submitted && $v.reportFile.$error }" />

                <div v-if="submitted && $v.reportFile.$error" class="invalid-feedback">
                  <span v-if="!$v.reportFile.required">{{ $t('validation.fileIsRequired') }}</span>
                </div>
              </div>

              <!-- Delete PDF -->
              <button v-if="reportFile" type="button" class="btn btn-outline-danger" @click="fileDel">DELETE</button>
            </div>

            <div v-if="reportFile" class="pt-2 text-black-50 ps-1">
              📄 {{ reportFile.name }}
            </div>

            <div class="text-black-50 ps-2 pt-2">- {{ $t('settlement.infoPdf') }}</div>
          </div>
        </div>

      </div>
    </div>

    <!-- Modal Footer -->
    <template #modal-footer>
      <div class="w-100 text-end">
        <b-button variant="secondary" class="w-xs p-1 me-3" @click="close">
          {{ $t('btn.close') }}
        </b-button>

        <b-button variant="success" class="w-xs p-1" @click="save">
          {{ $t('btn.save') }}
        </b-button>
      </div>
    </template>

  </b-modal>
</template>

<script>
import { http } from '@/auth-api';
import { required } from 'vuelidate/lib/validators';
import i18n from '@/i18n';
import Swal from 'sweetalert2';
import Multiselect from 'vue-multiselect';

export default {
  components: {
    Multiselect
  },
  data() {
    return {
      visible: false,
      submitted: false,
      cpOptions: [],
      cpId: null,
      form: {
        title: null,
      },
      reportFile: null,
    }
  },
  validations() {
    return {
      form: {
        title: { required },
      },
      reportFile: { required },
      cpId: { required },
    }
  },
  methods: {
    show() {
      const vm = this;

      vm.getCpOptions();
      this.visible = true;
    },
    getCpOptions() {
      const vm = this;
      const _url = '/onmapi/view/ChargePlace/list';

      vm.cpOptions = [{ text: i18n.t('common.total'), value: null }];

      http.post(_url, {
        page: 1,
        limit: 1000,
        searchConditions: [],
        sortCondition: { sortDirection: 'DESC' }
      }).then(res => {
        res.data.items.forEach(item => {
          vm.cpOptions.push({ text: item.placeName, value: item.placeId });
        });
      }).catch(err => {
        console.error('Charge Place List Error :: ', err)
      })
    },
    resetForm() {
      const vm = this;

      vm.cpOptions = [];
      vm.cpId = null;

      vm.form.title = null;
      vm.reportFile = null;

      if (this.$refs.pdfFileInput) {
        this.$refs.pdfFileInput.value = null;
      }

      vm.submitted = false;
    },
    close() {
      const vm = this;

      this.$emit('callback');
      vm.resetForm();
      vm.visible = false;
    },
    save() {
      const vm = this;
      vm.submitted = true;

      this.$v.$touch();

      if (!this.$v.$invalid) {
        Swal.fire({
          title: i18n.t('alert.confirmCreate'),
          showCancelButton: true,
          confirmButtonText: i18n.t('btn.ok'),
          cancelButtonText: i18n.t('btn.cancel')
        }).then((result) => {
          if (result.isConfirmed) {
            vm.saveItems();
          }
        });
      }
    },
    saveItems() {
      const vm = this;
      const _url = '/onmapi/view/Settlement/report/create';

      const _formData = new FormData();

      _formData.append('title', vm.form.title);
      _formData.append('file', vm.reportFile);

      if (!this.$_utils.isEmpty(vm.cpId)) {
        _formData.append('cpId', vm.cpId.value);
      } else {
        _formData.append('cpId', '');
      }

      http.post(_url, _formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(res => {
        if (res) {
          Swal.fire({
            icon: 'info',
            title: i18n.t('alert.created'),
            confirmButtonText: i18n.t('btn.close')
          });
          vm.close();
        }
      }).catch(err => {
        console.error('Report Create Error :: ', err)

        Swal.fire({
          icon: 'error',
          title: i18n.t('alert.error'),
          confirmButtonText: i18n.t('btn.close')
        });
      });
    },
    fileCheck(e) {
      const file = e.target.files[0];

      this.reportFile = null;

      if (!file) return;

      // 1. 확장자 검사 (.pdf)
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        Swal.fire('PDF 파일만 업로드할 수 있습니다.', '', 'error');
        this.$refs.pdfFileInput.value = null;
        return;
      }

      // 2. 사이즈 검사 (10MB)
      if (file.size > 1024 * 1024 * 10) {
        Swal.fire(
            i18n.t('alert.fileLimitMessage1'),
            i18n.t('alert.fileLimitMessage2'),
            'error'
        ).then(() => {
          this.reportFile = null;
          this.$refs.pdfFileInput.value = null;
        });
        return;
      }

      // 3. 저장
      this.reportFile = file;
    },
    fileDel() {
      this.reportFile = null;
      this.$refs.pdfFileInput.value = null;
    },
  }
}
</script>
